/**
 * `app/deeplink.ts` — acting on the `start_param` a launch carried. `spec.md` F15,
 * §12.3, `docs/verification-log.md` M12.
 *
 * ## Why this file had to exist
 *
 * Every other piece of the deep link already shipped: `packages/shared`'s
 * `parseDeepLink`, the `startParam` field on `POST /api/auth/telegram`'s response, the
 * zod guard on its alphabet, the facade getter, and the slot on `store/session`. The
 * value travelled the whole way from Telegram into the store — and **nothing read it**.
 * A launch from `t.me/<bot>/<app>?startapp=t<shortId>` landed on the crate and dropped
 * the track silently, which is what M12 records as a FAIL.
 *
 * ## The payload is read from the store, never from the query string
 *
 * §12.3 is explicit: take it from the **signed** `initDataUnsafe.start_param`, never
 * from the unsigned `tgWebAppStartParam` GET parameter. `store/session` holds the copy
 * the server echoed back out of the *validated* `initData`, so reading it here inherits
 * that guarantee rather than re-deriving it.
 *
 * ## A bad link lands on the home screen, never on an error
 *
 * §12.3 says so outright, and `parseDeepLink` is built for it: it returns `null` rather
 * than throwing for `undefined`, the empty string, an unknown tag, a malformed short id
 * and anything out of alphabet. Every failure path here — a parse miss, a track the
 * catalogue no longer has, an offline fetch — resolves to an outcome the caller ignores
 * and leaves the user in the crate. Nothing on this path may reject.
 *
 * ## Only `t` can be resolved today, and that is a fact about the API, not a shortcut
 *
 * `DEEP_LINK_KINDS` is `['t', 'p', 'a']`, but of the three only the track is reachable
 * from a short id: §6.4 gives `GET /api/tracks/:id` "either a numeric id or a
 * `shortId`", while `/api/playlists/:id` and `/api/artists/:id` both bind
 * `NumericIdParams` and there is no short-id lookup for either. So `p` and `a` return
 * {@link DEEP_LINK_UNSUPPORTED} and land on home — the §12.3 behaviour — instead of
 * this file inventing an endpoint. Adding a short-id route for them is what makes them
 * work; nothing here needs to change but the switch.
 *
 * ## It opens the player; it does not start it
 *
 * F15 says the link "opens the player directly on a track", and §13.2 says a resume or
 * an autoplay may not happen before a gesture — the tap happened in Telegram's chat
 * list, not in this webview, so the element's playback restriction is still armed. The
 * two are satisfied together by making the track the queue's current item and routing
 * to `/now`, leaving the play button to the user. `wiring.select()` is deliberately
 * NOT called.
 *
 * The queue is replaced rather than appended, because `replace(tracks, index)` is this
 * app's own idiom for "pull this record" — `Crate`, `MyCrate`, `Artist` and `Tag` all
 * play through it — and a tapped share link is as explicit a request for one record as
 * a tapped row is.
 */

import { parseDeepLink } from '@bl/shared';
import type { TrackSummary } from '@bl/shared';

import { api } from '../lib/api';
import type { ApiClient } from '../lib/api';
import { useQueueStore } from '../store/queue';

import { buildPath, navigate } from './router';

/** The link named a track and the player is now showing it. */
export const DEEP_LINK_TRACK = 'track';
/** The `home` payload, or a launch with no payload at all: stay in the crate. */
export const DEEP_LINK_HOME_OUTCOME = 'home';
/** A `p` or `a` payload, which no short-id route can resolve yet. */
export const DEEP_LINK_UNSUPPORTED = 'unsupported';
/** Unparseable — §12.3's "a bad deep link must land on the home screen". */
export const DEEP_LINK_IGNORED = 'ignored';
/** Parsed and supported, but the lookup or the queue write failed. */
export const DEEP_LINK_FAILED = 'failed';

export type DeepLinkOutcome =
  | typeof DEEP_LINK_TRACK
  | typeof DEEP_LINK_HOME_OUTCOME
  | typeof DEEP_LINK_UNSUPPORTED
  | typeof DEEP_LINK_IGNORED
  | typeof DEEP_LINK_FAILED;

/**
 * Injectable seams, in the shape the rest of `app/*` uses: a real default per field so
 * production calls `openDeepLink(startParam)` with no second argument at all.
 */
export interface DeepLinkDeps {
  readonly client?: Pick<ApiClient, 'track'> | undefined;
  readonly replaceQueue?:
    | ((tracks: readonly TrackSummary[], index?: number) => Promise<void>)
    | undefined;
  readonly go?: ((path: string) => void) | undefined;
}

/**
 * Act on a `start_param`. Resolves to what happened and **never rejects**: the caller
 * is `boot()`, and a share link that no longer resolves must not take the app down
 * with it.
 *
 * @param startParam the payload from `store/session`, straight off the signed
 *   `initData` — not from `location.search`.
 */
export async function openDeepLink(
  startParam: string | null | undefined,
  deps: DeepLinkDeps = {},
): Promise<DeepLinkOutcome> {
  const target = parseDeepLink(startParam);
  if (target === null) return DEEP_LINK_IGNORED;
  if (target.kind === 'home') return DEEP_LINK_HOME_OUTCOME;
  if (target.kind !== 't') return DEEP_LINK_UNSUPPORTED;

  const client = deps.client ?? api;
  const replaceQueue =
    deps.replaceQueue ??
    ((tracks: readonly TrackSummary[], index?: number) =>
      useQueueStore.getState().replace(tracks, index));
  const go = deps.go ?? ((path: string) => navigate(path, { replace: true }));

  try {
    const track = await client.track(target.shortId);
    await replaceQueue([track], 0);
    go(buildPath('now'));
    return DEEP_LINK_TRACK;
  } catch {
    // §12.3 again: the crate is the fallback for everything, including a share of a
    // track the channel has since deleted. The failure is the outcome, not a throw.
    return DEEP_LINK_FAILED;
  }
}
